import CsvImportOption from "@/types/CsvImportOption.interface"
import CsvProgress from "@/types/CsvProgress.interface"
import CsvRow from "@/types/CsvRow.interface"
import PersonProperty from "@/types/PersonProperty.interface"
import Setting from "@/types/Setting.interface"
import { randomKey } from '@/utility/helpers'
import csvhelper from '@/plugins/csvhelper'

interface State {
    csvData: CsvRow[]
    csvExportProgress: CsvProgress
    csvFileName: string
    csvHeaders: string[]
    csvImportProgress: CsvProgress
    csvRowData: CsvRow
    importGroup: number
    importMatched: CsvImportOption[]
    importOptions: CsvImportOption[]
    importOverwrite: boolean
    importReport: string
    importSearch: string
}

const defaults = () => {
    return {
        csvData: null,           //  Array of all rows in the csv file
        csvExportProgress: null, //  The progress of a currently running csv export
        csvFileName: null,       //  The filename of the csv file to be imported
        csvHeaders: null,        //  The columns headers extracted from the selected csv file
        csvImportProgress: null, //  The progress of a currently running csv import
        csvRowData: null,        //  A single line of data from the selected csv file, used to display sample data
        importGroup: null,       //  The id of the group to put imported contacts into
        importMatched: [],       //  Person fields that have been mapped to a field from the csv file
        importOptions: [],       //  All person fields auto-mapped to a field from the csv file
        importOverwrite: true,   //  Determines if existing data is overwritten during the csv import
        importReport: null,      //  The csv import report received in the CsvImportCompleted user notification
        importSearch: null
    }
}

export default {
    state: defaults(),
    mutations: {
        csvDataSet(state: State, array: CsvRow[]): void {
            state.csvData = array
        },
        csvExportProgressSet(state: State, obj: CsvProgress): void {
            state.csvExportProgress = obj
        },
        csvFileNameSet(state: State, str: string): void {
            state.csvFileName = str
        },
        csvImportProgressSet(state: State, obj: CsvProgress): void {
            state.csvImportProgress = obj
        },
        importGroupSet(state: State, id: number): void {
            state.importGroup = id
        },
        importMatchedSet(state: State, array: CsvImportOption[]): void {
            state.importMatched = array
        },
        importOptionsAdd(state: State, property: PersonProperty): void {
            //  Create a new mapping with property
            const map = {
                uuid: randomKey(),
                sdkField: property,
                csvField: null,
                csvValue: null
            }

            //  Check if a csv header matches a property name
            const index = state.csvHeaders.indexOf(property.name)

            //  Check if we are already using this prop
            const used = state.importOptions.find(x => x.csvField === property.name)

            //  If a match is found, prepopulate the csv header and data
            if (index > -1 && !used) {
                map.csvField = state.csvHeaders[index]
                map.csvValue = state.csvRowData[state.csvHeaders[index]]
            }

            //  Add the line to the matched array
            state.importOptions.push(map)
        },
        importOverwriteSet(state: State, bool: boolean): void {
            state.importOverwrite = bool
        },
        importReportSet(state: State, report: string): void {
            state.importReport = report
        },
        importReset(state: State): void {
            Object.assign(state, defaults())
        },
        importSearchSet(state: State, field: string): void {
            state.importSearch = field
        },
        csvReset(state: State): void {
            state.csvData = null
            state.csvHeaders = null
            state.csvRowData = null
            state.importOverwrite = true
            state.importMatched = []
            state.importOptions = [],
                state.importSearch = null
        },
        csvRowDataSet(state: State, object: CsvRow): void {
            //  Set row data
            state.csvRowData = object

            //  Set csv headers
            state.csvHeaders = Object.keys(object)
        }
    },
    actions: {
        csvDownload({ }, url: string): boolean {
            try {
                //  Create a link
                const link = document.createElement("a");

                //  Set the link target to the pre-signed S3 url
                link.href = url;

                //  Make Safari open the csv in a different page, otherwise it will redirect the user away from the app.
                link.target = '_blank'

                //  Add the link to the body
                document.body.appendChild(link);

                //  Download the csv file by clicking the link
                link.click();

                //  Remove the temporary link for the body
                document.body.removeChild(link);

                return true
            } catch (e) {
                return null
            }
        },
        csvInit({ commit, getters }): void {
            getters.personProperties.forEach((property: PersonProperty) => {
                commit('importOptionsAdd', property)
            })
        },
        csvMap({ getters }): Promise<Setting> {
            //  Convert form data into mapped object
            const matched = csvhelper.toMappedObject(getters.importMatched)

            //  Assemble the payload
            const payload = {
                group_id: getters.importGroup,
                overwrite: getters.importOverwrite,
                matched: matched,
                search: getters.importSearch
            }

            //  Assemble the API endpoint
            const url = `${getters.api}/utilities/csv/map`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Return our found CSV mapping setting
                return response.data
            }).catch(() => null)
        },
        csvExport({ getters }, selected: PersonProperty[]): Promise<object> {
            //  Assemble the API endpoint
            const url = `${getters.api}/utilities/csv/export`

            //  Send the request
            return window.axios.post(url, selected).then(response => {
                //  
                return response.data
            }).catch(() => null)
        },
        csvImport({ commit, getters }): Promise<object> {
            //  Create our payload based on our current csv data
            const payload = { rows: getters.csvData }

            //  Assemble the API endpoint
            const url = `${getters.api}/utilities/csv/import`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Reset the import form
                commit('csvReset')

                //  Return csv report
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        csvData(state: State): CsvRow[] {
            return state.csvData
        },
        csvExportProgress(state: State): CsvProgress {
            return state.csvExportProgress
        },
        csvFileName(state: State): string {
            return state.csvFileName
        },
        csvHeaders(state: State): string[] {
            return state.csvHeaders
        },
        csvImportProgress(state: State): CsvProgress {
            return state.csvImportProgress
        },
        importReport(state: State): string {
            return state.importReport
        },
        importGroup(state: State): number {
            return state.importGroup
        },
        importMatched(state: State): CsvImportOption[] {
            return state.importMatched
        },
        importOptions(state: State): CsvImportOption[] {
            return state.importOptions
        },
        importOverwrite(state: State): boolean {
            return state.importOverwrite
        },
        importSearch(state: State): string {
            return state.importSearch
        },
        csvRowData(state: State): CsvRow {
            return state.csvRowData
        }
    }
}